import React, { useEffect, useState } from 'react';
import {
    Row,
    Col,
    Panel,
    Table, ButtonToolbar, Button, ButtonGroup, FormControl, Alert
} from 'react-bootstrap';
import { connect } from 'react-redux';
import { useFeatureflow } from 'react-featureflow-client';
import { withRouter } from 'react-router';
import * as featureApi from '../api/feature';
import type { Feature } from '../api/feature';
import type { Project } from '../api/project';
import ProjectPanel from "../components/project/ProjectPanel";
import FeatureSummaryRow from "../components/features/FeatureSummaryRow";
import { LinkContainer } from "react-router-bootstrap";
import { useGetProjectsQuery } from '../api/featureflowApi';
import { LoadingOverlay } from '../components/LoadingOverlay';

interface HomeProps {
    location: {
        query: {
            query?: string;
        };
        pathname: string;
        search: string;
        state: any;
        hash: string;
    };
    history: any;
    match: any;
}

interface MapStateToProps {
    // Add any Redux state props here if needed
}

function mapStateToProps(state: any, props: HomeProps): MapStateToProps {
    return {};
}

const Home: React.FC<HomeProps> = (props) => {
    const BOOKMARKED = 'bookmarked';
    const RECENT = 'recent';
    const MAINTAINING = 'maintaining';
    let query = props.location.query.query || 'recent';

    const [features, setFeatures] = useState<Feature[]>([]);
    const [filteredFeatures, setFilteredFeatures] = useState<Feature[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentQuery, setCurrentQuery] = useState(query);
    const [sortField, setSortField] = useState<'key' | 'name' | 'description' | 'project' | 'maintainer'>('name');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [filterText, setFilterText] = useState('');

    const { data: projects = [], error, isLoading: isProjectsLoading } = useGetProjectsQuery({});
    const featureflow = useFeatureflow();

    const handleSort = (field: 'key' | 'name' | 'description' | 'project' | 'maintainer') => {
        if (field === sortField) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleFilter = (text: string) => {
        setFilterText(text);
    };

    const handleFormControlChange = (e: any) => {
        handleFilter(e.target.value);
    };

    useEffect(() => {
        if (features.length > 0) {
            let filtered = [...features];
            
            // Apply filter
            if (filterText) {
                filtered = filtered.filter(feature => 
                    feature.name.toLowerCase().includes(filterText.toLowerCase()) ||
                    feature.key.toLowerCase().includes(filterText.toLowerCase()) ||
                    feature.description.toLowerCase().includes(filterText.toLowerCase())
                );
            }

            // Apply sort
            filtered.sort((a, b) => {
                let aValue = a[sortField];
                let bValue = b[sortField];
                
                // Special handling for project sorting
                if (sortField === 'project') {
                    aValue = a.project?.name || '';
                    bValue = b.project?.name || '';
                }
                
                const direction = sortDirection === 'asc' ? 1 : -1;
                
                // Convert to string and handle null/undefined cases
                const aStr = String(aValue || '');
                const bStr = String(bValue || '');
                
                return aStr.localeCompare(bStr) * direction;
            });

            setFilteredFeatures(filtered);
        }
    }, [features, sortField, sortDirection, filterText]);

    const loadFiltered = async (query: string) => {
        if (isLoading) {
            return;
        }
        setIsLoading(true);
        try {
            if (query === RECENT) {
                const recentFeatures = await featureApi.getRecent();
                setFeatures(recentFeatures);
            }
            if (query === BOOKMARKED) {
                const bookmarkedFeatures = await featureApi.getBookmarked();
                setFeatures(bookmarkedFeatures);
            }
            if (query === MAINTAINING) {
                const maintainingFeatures = await featureApi.getMaintaining();
                setFeatures(maintainingFeatures);
            }
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        setCurrentQuery(query);
        loadFiltered(query);
    }, [query]);

    return (
        <>
            <LoadingOverlay visible={isLoading} />
            {featureflow.evaluate('evolved-banner').isOn() &&
            <Row>
                <Col xs={12}>
                    <Alert bsStyle="info">
                        <strong>Featureflow has evolved!</strong>{' '}
                        Log in to the new Featureflow with your existing credentials at{' '}
                        <a href="https://app.featureflow.com" target="_blank" rel="noopener noreferrer" className="alert-link">app.featureflow.com</a>{' '}
                        and enjoy an enhanced experience with approvals, pipelines and a better way to manage your features.
                    </Alert>
                </Col>
            </Row>
            }
            <Row>
                <Col xs={12} md={8} style={{paddingBottom: 10}}>
                    <h2 style={{paddingBottom: 0}}>
                        Home
                    </h2>
                </Col>
            </Row>
            {featureflow.evaluate('home-projects').isOn() &&
            <>
                <Row>
                    <Col xs={12} md={8} style={{paddingBottom: 10}}>
                        <h3 style={{paddingBottom: 0}}>
                            Projects
                        </h3>
                    </Col>
                </Row>
                <Row>
                    {projects.map((project: Project) =>
                        <Col xs={6} md={3} style={{paddingBottom: 20}} key={project.id}>
                            <Panel>
                                <Panel.Heading>
                                    <Panel.Title componentClass="h3">{project.name}</Panel.Title>
                                </Panel.Heading>
                                <Panel.Body>
                                    <ProjectPanel project={project}></ProjectPanel>
                                </Panel.Body>
                            </Panel>
                        </Col>
                    )}
                </Row>
            </>
            }
            <Row>
                <Col xs={12} style={{paddingBottom: 20}}>
                    {featureflow.evaluate('home-projects').isOn() &&
                    <h3>Features</h3>
                    }
                    <Row>
                        <Col lg={8}>
                            <div className="bs-component">
                                <ButtonToolbar>
                                    <ButtonGroup bsSize="small">
                                        <LinkContainer to='/home?query=recent'>
                                            <Button
                                                active={currentQuery === RECENT}
                                                bsStyle={currentQuery === RECENT ? 'primary active' : 'primary'}
                                                disabled={isLoading}>Recent</Button>
                                        </LinkContainer>
                                        <LinkContainer to={{pathname: `/home`, search: "?query=bookmarked"}}>
                                            <Button bsStyle="primary"
                                                    disabled={isLoading}
                                                    active={currentQuery === BOOKMARKED}>Bookmarked</Button>
                                        </LinkContainer>
                                        <LinkContainer to={{pathname: `/home`, search: "?query=maintaining"}}>
                                            <Button bsStyle="primary"
                                                    disabled={isLoading}
                                                    active={currentQuery === MAINTAINING}>Maintaining</Button>
                                        </LinkContainer>
                                    </ButtonGroup>
                                </ButtonToolbar>
                            </div>
                        </Col>
                        <Col lg={4}>
                            <div style={{ margin: '10px 0' }}>
                                <FormControl
                                    bsSize="sm"
                                    type="text"
                                    placeholder="Filter Features..."
                                    value={filterText}
                                    onChange={handleFormControlChange}
                                />
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={12}>
                            <Table className="table table-striped table-hover">
                                <thead>
                                    <tr>
                                        <th style={{border: 0, cursor: 'pointer'}} onClick={() => handleSort('key')}>
                                            Key {sortField === 'key' && (sortDirection === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th style={{border: 0, cursor: 'pointer'}} onClick={() => handleSort('name')}>
                                            Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th style={{border: 0, cursor: 'pointer'}} onClick={() => handleSort('description')}>
                                            Description {sortField === 'description' && (sortDirection === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th style={{border: 0, cursor: 'pointer'}} onClick={() => handleSort('project')}>
                                            Project {sortField === 'project' && (sortDirection === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th style={{border: 0, cursor: 'pointer'}} onClick={() => handleSort('maintainer')}>
                                            Maintainer {sortField === 'maintainer' && (sortDirection === 'asc' ? '↑' : '↓')}
                                        </th>
                                        <th style={{border: 0}} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {(!filteredFeatures || filteredFeatures.length === 0) &&
                                    <tr>
                                        <td colSpan={6}>
                                            <div style={{margin: 50, textAlign: 'center', fontSize: 20}}>
                                                No results
                                            </div>
                                        </td>
                                    </tr>
                                    }
                                    {filteredFeatures.map(feature => <FeatureSummaryRow key={feature.id} feature={feature}/>)}
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </>
    );
};

export default withRouter(connect(mapStateToProps)(Home)); 